import { call, takeEvery, put, select } from 'redux-saga/effects'
import { buyFormActions, BuyFormActionTypes } from '@buy/state/buy-form/actions'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { BuyCheckoutState } from '@buy/state/checkout/state'
import { getCartItems } from '@buy/utils/get-item-values'
import {
    getCheckoutState,
    getUserState,
    getCouponState,
    getPlanData,
    getProductSettingsData,
} from '@buy/state/selectors'
import { getLocationState, getAccountState } from '@common/state/selectors'
import { CHECKOUTFORM_STEPS } from '@buy/constants'
import { LocationState } from '@common/state/location/state'
import {
    ApplyPromoCodeRequest,
    BuyUserState,
    CreateCartRequest,
    CreateV3CartRequestCartItem,
    CreateCartResponse,
} from '@buy/state/user/state'
import { ExistingUserInfoType } from '@common/state/user/state'
import { BuyPlan } from '@buy/state/plan'
import { BuyCouponState } from '@buy/state/coupon/state'
import { isEmpty } from 'lodash-es'
import { getSubscriptionInfo } from '@buy/utils'
import { getTokenFromLocalStorage, removeLocalStorageAuthItems } from '@common/utils'
import { AxiosResponse } from 'axios'
import { applyPromoCode, createCartV3, deleteCart } from '@buy/services'
import { ProductSettings } from '@buy/state/product-settings'

function* submitFormSaga() {
    const steps: Record<CHECKOUTFORM_STEPS, boolean> = {
        [CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN]: true,
        [CHECKOUTFORM_STEPS.CARDINAL_TRIGGER_BIN_PROCESS]: true,
        [CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]: true,
        [CHECKOUTFORM_STEPS.SAVE_BILLING_INFO]: true,
        [CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO]: true,
        [CHECKOUTFORM_STEPS.CARDINAL_SEND_CHALLENGE]: false,
        [CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]: false,
        [CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]: false,
    }
    // update the redux store to ensure consistency
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN, steps[CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN])
    )
    yield put(
        buyFormActions.updateSteps(
            CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT,
            steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]
        )
    )
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SAVE_BILLING_INFO, steps[CHECKOUTFORM_STEPS.SAVE_BILLING_INFO])
    )
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO, steps[CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO])
    )
    yield put(fatalErrorActions.setFatalError(false))
    const checkout: BuyCheckoutState = yield select(getCheckoutState)
    const { adminUrl }: ProductSettings = yield select(getProductSettingsData)
    const { locationInfo }: LocationState = yield select(getLocationState)
    const { productFamily }: ProductSettings = yield select(getProductSettingsData)

    const userState: BuyUserState = yield select(getUserState)

    const { AccountKey } = userState.existingUserInformation as ExistingUserInfoType

    const planData: BuyPlan = yield select(getPlanData)
    const selectedPlan: BuyPlan = planData
    const coupon: BuyCouponState = yield select(getCouponState)
    const { isCouponValidAndApplied, couponServerData } = coupon
    // const couponServerData: CouponServerData | null = yield call(getActiveCouponServerData, coupon, planData)
    const isCouponValid = isCouponValidAndApplied && couponServerData && !isEmpty(couponServerData)

    try {
        const { accountSubscriptions } = yield select(getAccountState)

        const subscriptionInfo = getSubscriptionInfo(
            checkout.billingTenure.selected,
            selectedPlan,
            planData,
            accountSubscriptions
        )

        const items = getCartItems(
            checkout.baseLine,
            checkout.additionalLines,
            checkout.addonLines,
            subscriptionInfo,
            checkout.billingTenure.selected,
            productFamily
        )

        // #region get available payment menthods
        steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS] = true
        yield put(
            buyFormActions.updateSteps(
                CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS,
                steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]
            )
        )
        // #endregion

        let cartId = 'missing_cart_id' // default value for troubleshooting in case cartId doesn't get updated updated below

        // #region process purchase - 1st purchase call
        if (steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]) {
            // #region Cart and Transaction
            const promoCode = isCouponValid ? couponServerData?.couponCode : ''
            const token = getTokenFromLocalStorage()!

            // Delete any existing cart
            const deleteCartResponse: AxiosResponse<boolean> = yield call(
                deleteCart as any,
                token.access_token,
                AccountKey
            )
            if (!(deleteCartResponse.status === 404 || deleteCartResponse.status === 200)) {
                // 404 - Commerce cart not found, so you're got to create a new cart
                throw new Error('Error while invoking DELETE v2/cart') // throw an exception if any other errors in the API
            }

            // Create Cart
            const cartItems = items.map((item) => {
                const cartItem: CreateV3CartRequestCartItem = {
                    paymentPlan: item.paymentPlan,
                    productFamilyKey: productFamily,
                    productSku: item.productSku,
                    quantity: item.quantity,
                }
                return cartItem
            })
            const cartRequestData: CreateCartRequest = {
                currency: locationInfo.currency,
                cartItems,
                promoCode,
            }

            try {
                const createCartResponse: AxiosResponse<CreateCartResponse> = yield call(
                    createCartV3 as any,
                    token.access_token,
                    AccountKey,
                    cartRequestData
                )
                cartId = createCartResponse.data.key
            } catch (ex) {
                // If there was an error creating the cart, redirect to the admin site
                // console.log('Create cart failed. Redirecting to admin site.')
                removeLocalStorageAuthItems()
                window.location.href = adminUrl
                return
            }

            // Apply promoCode (if any) to the cart
            if (promoCode) {
                const applyPromoCodeRequestData: ApplyPromoCodeRequest = {
                    promoCode,
                }
                const applyPromoCodeResponse: AxiosResponse = yield call(
                    applyPromoCode as any,
                    token.access_token,
                    AccountKey,
                    applyPromoCodeRequestData
                )
                if (applyPromoCodeResponse.status !== 200) {
                    // TODO: WAE1-4010: Errror handling?
                }
            }

            // Proceed to post-submit_payment steps
            steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT] = true
            yield put(
                buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SUBMIT_PAYMENT, steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT])
            )
        }
        // #endregion

        // #region Set purchase complete
        if (steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]) {
            // Redirect to the cart on the admin site
            removeLocalStorageAuthItems()
            const adminUrlBase = new URL(adminUrl).origin
            const adminCartUrl = `${adminUrlBase}/billing/subscriptions/${cartId}/checkout`
            window.location.href = adminCartUrl
            return
        }
        // #endregion

        // Trigger form submission error if any of the steps are missed
        yield put(
            fatalErrorActions.setFatalError(true, {
                trackErrorMessage: 'existing user form submission failed',
                fatalErrorType: undefined,
            })
        )
    } catch (e) {
        yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'existing user form submission failed' }))
    }
}

function* initializeSubmitFormSaga() {
    yield takeEvery(BuyFormActionTypes.EXISTING_USER_SUBMIT_BUY_FORM_UCS, submitFormSaga)
}

export default initializeSubmitFormSaga
